import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Users,
  User,
  Calendar,
  Clock,
  Search,
  ChevronRight,
  Mail,
  Phone,
  ArrowLeft,
  RefreshCw,
  Wrench,
  CheckCircle,
  Copy,
  Check,
  AlertTriangle,
  Send,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Centralised membership-tier badge so the roster + detail view show the
// same 5-tier classification at a glance.
const TIER_STYLES = {
  section: { cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30", label: "Section Member" },
  club_verified: { cls: "bg-blue-500/20 text-blue-300 border-blue-500/30", label: "Verified SCC Member" },
  club_pending: { cls: "bg-amber-500/20 text-amber-300 border-amber-500/30", label: "SCC — Pending Verify" },
  digital: { cls: "bg-purple-500/20 text-purple-300 border-purple-500/30", label: "Digital Member" },
  non_member: { cls: "bg-slate-700/40 text-slate-300 border-slate-600/40", label: "Non-member" },
};

function MembershipTierBadge({ parent, compact }) {
  if (!parent) return null;
  const tier = parent.membership_tier || "non_member";
  const meta = TIER_STYLES[tier] || TIER_STYLES.non_member;
  const label = parent.membership_tier_label || meta.label;
  return (
    <Badge
      className={`${meta.cls} ${compact ? "mt-1 text-[10px]" : "text-xs"} border`}
      data-testid={`tier-badge-${parent.id || ""}`}
      title={parent.membership_number ? `Membership #${parent.membership_number}` : undefined}
    >
      {label}
    </Badge>
  );
}

export default function AcademyRoster() {
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("sessions"); // "sessions" or "parents"
  const [searchTerm, setSearchTerm] = useState("");
  
  // Sessions view data
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionPlayers, setSessionPlayers] = useState([]);
  const [paymentStatuses, setPaymentStatuses] = useState({});
  
  // Parents view data
  const [parents, setParents] = useState([]);
  const [selectedParent, setSelectedParent] = useState(null);
  const [parentChildren, setParentChildren] = useState([]);
  
  // Detail dialog
  const [detailDialog, setDetailDialog] = useState({ open: false, type: null, data: null });
  
  // Fix membership numbers
  const [fixingMemberships, setFixingMemberships] = useState(false);
  const [fixResultDialog, setFixResultDialog] = useState({ open: false, data: null });
  const [copiedField, setCopiedField] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Multi-select for bulk email
  const [selectedParentIds, setSelectedParentIds] = useState([]);
  const [sendingBulkEmail, setSendingBulkEmail] = useState(false);

  const toggleParentSelection = (parentId) => {
    setSelectedParentIds(prev => 
      prev.includes(parentId) 
        ? prev.filter(id => id !== parentId)
        : [...prev, parentId]
    );
  };

  const selectAllParents = () => {
    if (selectedParentIds.length === filteredParents.length) {
      setSelectedParentIds([]);
    } else {
      setSelectedParentIds(filteredParents.map(p => p.id));
    }
  };

  const sendBulkParentInfoEmail = async () => {
    if (selectedParentIds.length === 0) {
      toast.error("Please select at least one parent");
      return;
    }
    
    setSendingBulkEmail(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await axios.post(`${API}/admin/send-parent-info-email-bulk`, 
        { parent_ids: selectedParentIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { success, failed, errors } = res.data;
      if (failed > 0) {
        toast.warning(`Sent ${success} emails, ${failed} failed`);
        console.log("Failed emails:", errors);
      } else {
        toast.success(`Successfully sent ${success} emails!`);
      }
      setSelectedParentIds([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send bulk emails");
    } finally {
      setSendingBulkEmail(false);
    }
  };

  const sendParentInfoEmail = async (parentId) => {
    setSendingEmail(true);
    try {
      const token = localStorage.getItem("session_token");
      await axios.post(`${API}/admin/send-parent-info-email/${parentId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Email sent with login info and children's registration numbers!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const copyToClipboard = (text, fieldId) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldId);
    setTimeout(() => setCopiedField(null), 2000);
    toast.success("Copied!");
  };

  const handleFixMissingMemberships = async () => {
    setFixingMemberships(true);
    try {
      const response = await axios.post(`${API}/academy/admin/fix-missing-membership-numbers`, {}, {
        withCredentials: true
      });
      
      if (response.data.fixed_count > 0) {
        setFixResultDialog({ open: true, data: response.data });
      } else {
        toast.success("All players already have membership numbers!");
      }
      
      // Refresh data
      fetchData();
    } catch (error) {
      console.error("Error fixing memberships:", error);
      toast.error(error.response?.data?.detail || "Failed to fix membership numbers");
    } finally {
      setFixingMemberships(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch sessions with player counts
      const sessionsRes = await axios.get(`${API}/academy/admin/sessions-roster`, {
        withCredentials: true
      });
      setSessions(sessionsRes.data || []);
      
      // Fetch parents with children
      const parentsRes = await axios.get(`${API}/academy/admin/parents-roster`, {
        withCredentials: true
      });
      setParents(parentsRes.data || []);
      
    } catch (error) {
      console.error("Error fetching roster data:", error);
      // Use fallback data if endpoints not ready
      toast.error("Failed to load roster data");
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionPlayers = async (sessionId, termId) => {
    try {
      const res = await axios.get(`${API}/academy/admin/session/${sessionId}/players`, {
        withCredentials: true
      });
      const list = res.data || [];
      setSessionPlayers(list);
      // Paid/unpaid marker (same source of truth as Holding Group)
      const ids = list.map(p => p.id).filter(Boolean);
      if (ids.length) {
        try {
          const ps = await axios.get(
            `${API}/academy/coach/player-payment-status?player_ids=${ids.join(',')}${termId ? `&term_id=${termId}` : ''}`,
            { withCredentials: true }
          );
          const map = {};
          (ps.data.players || []).forEach(p => { map[p.player_id] = p; });
          setPaymentStatuses(map);
        } catch (_) { setPaymentStatuses({}); }
      } else {
        setPaymentStatuses({});
      }
    } catch (error) {
      console.error("Error fetching session players:", error);
      setSessionPlayers([]);
    }
  };

  const fetchParentChildren = async (parentId) => {
    try {
      const res = await axios.get(`${API}/academy/admin/parent/${parentId}/children`, {
        withCredentials: true
      });
      setParentChildren(res.data || []);
    } catch (error) {
      console.error("Error fetching parent children:", error);
      setParentChildren([]);
    }
  };

  const handleSessionClick = (session) => {
    setSelectedSession(session);
    fetchSessionPlayers(session.id, session.term_id);
  };

  const handleParentClick = (parent) => {
    setSelectedParent(parent);
    fetchParentChildren(parent.id);
  };

  // Filter logic
  const filteredSessions = sessions.filter(s => 
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.day_of_week?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.age_group_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [tierFilter, setTierFilter] = useState("all");

  const filteredParents = parents.filter(p => {
    const matchesQuery = p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.email?.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesQuery) return false;
    if (tierFilter === "all") return true;
    return (p.membership_tier || "non_member") === tierFilter;
  });

  const tierCounts = parents.reduce((acc, p) => {
    const t = p.membership_tier || "non_member";
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white p-6">
      {/* Fix Result Dialog */}
      <Dialog open={fixResultDialog.open} onOpenChange={(open) => setFixResultDialog({ open, data: open ? fixResultDialog.data : null })}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-400">
              <CheckCircle className="w-6 h-6" />
              Membership Numbers Generated!
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {fixResultDialog.data?.message}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4 max-h-[400px] overflow-y-auto">
            {fixResultDialog.data?.players?.map((player, idx) => (
              <div key={idx} className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                <div className="font-medium text-white mb-2">{player.name}</div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-400 text-xs">Membership #</span>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="bg-amber-500/20 text-amber-400 px-2 py-1 rounded font-mono font-bold">
                        {player.membership_number}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(player.membership_number, `mem-${idx}`)}
                        className="h-7 w-7 p-0"
                      >
                        {copiedField === `mem-${idx}` ? (
                          <Check className="h-4 w-4 text-emerald-400" />
                        ) : (
                          <Copy className="h-4 w-4 text-slate-400" />
                        )}
                      </Button>
                    </div>
                  </div>
                  {player.temporary_password && (
                    <div>
                      <span className="text-slate-400 text-xs">Temp Password</span>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="bg-slate-700 px-2 py-1 rounded font-mono text-sm">
                          {player.temporary_password}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(player.temporary_password, `pass-${idx}`)}
                          className="h-7 w-7 p-0"
                        >
                          {copiedField === `pass-${idx}` ? (
                            <Check className="h-4 w-4 text-emerald-400" />
                          ) : (
                            <Copy className="h-4 w-4 text-slate-400" />
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                {player.user_created && (
                  <Badge className="mt-2 bg-blue-500/20 text-blue-400 text-xs">New login account created</Badge>
                )}
              </div>
            ))}
          </div>
          
          <DialogFooter>
            <Button 
              onClick={() => setFixResultDialog({ open: false, data: null })}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold">Academy Roster</h1>
              <p className="text-sm text-slate-400">
                Track players, parents, and session assignments
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={handleFixMissingMemberships} 
              disabled={fixingMemberships}
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
              title="Generate membership numbers for players who don't have them"
            >
              {fixingMemberships ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Wrench className="w-4 h-4 mr-2" />
              )}
              Fix Missing Memberships
            </Button>
            <Button variant="outline" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* View Toggle */}
        <Tabs value={activeView} onValueChange={setActiveView} className="mb-6">
          <TabsList className="bg-slate-800">
            <TabsTrigger value="sessions" className="data-[state=active]:bg-blue-600">
              <Calendar className="w-4 h-4 mr-2" />
              Sessions → Players
            </TabsTrigger>
            <TabsTrigger value="parents" className="data-[state=active]:bg-emerald-600">
              <Users className="w-4 h-4 mr-2" />
              Parents → Children
            </TabsTrigger>
          </TabsList>

          {/* Search */}
          <div className="mt-4 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder={activeView === "sessions" ? "Search sessions, days, age groups..." : "Search parents by name or email..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-900 border-slate-700"
            />
          </div>

          {/* Sessions View */}
          <TabsContent value="sessions" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Sessions List */}
              <div className="lg:col-span-1">
                <Card className="bg-slate-900/50 border-slate-700 p-4">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-blue-400" />
                    Sessions ({filteredSessions.length})
                  </h3>
                  
                  {loading ? (
                    <div className="text-center py-8 text-slate-400">Loading...</div>
                  ) : filteredSessions.length === 0 ? (
                    <div className="text-center py-8 text-slate-400">No sessions found</div>
                  ) : (
                    <div className="space-y-2 max-h-[600px] overflow-y-auto">
                      {filteredSessions.map((session) => (
                        <button
                          key={session.id}
                          onClick={() => handleSessionClick(session)}
                          className={`w-full text-left p-3 rounded-lg border transition-colors ${
                            selectedSession?.id === session.id
                              ? 'bg-blue-500/20 border-blue-500'
                              : 'bg-slate-800/50 border-slate-700 hover:bg-slate-800'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{session.name || session.day_of_week}</p>
                              <p className="text-xs text-slate-400">
                                {session.day_of_week} {session.start_time}-{session.end_time}
                              </p>
                              {session.age_group_name && (
                                <Badge className="mt-1 bg-purple-500/20 text-purple-300 text-xs">
                                  {session.age_group_name}
                                </Badge>
                              )}
                            </div>
                            <div className="text-right">
                              <span className="text-lg font-bold text-blue-400">
                                {session.player_count || 0}
                              </span>
                              <p className="text-xs text-slate-400">players</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </Card>
              </div>

              {/* Session Detail - Players List */}
              <div className="lg:col-span-2">
                <Card className="bg-slate-900/50 border-slate-700 p-4">
                  {selectedSession ? (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="font-semibold text-lg">{selectedSession.name || selectedSession.day_of_week}</h3>
                          <p className="text-sm text-slate-400">
                            {selectedSession.day_of_week} {selectedSession.start_time}-{selectedSession.end_time}
                            {selectedSession.venue && ` • ${selectedSession.venue}`}
                          </p>
                        </div>
                        <Badge className="bg-blue-500/20 text-blue-400">
                          {sessionPlayers.length} Players
                        </Badge>
                      </div>

                      {sessionPlayers.length === 0 ? (
                        <div className="text-center py-12 text-slate-400">
                          <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>No players in this session</p>
                        </div>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Player</TableHead>
                              <TableHead>Age</TableHead>
                              <TableHead>Parent</TableHead>
                              <TableHead>Contact</TableHead>
                              <TableHead>Payment</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {sessionPlayers.map((player) => (
                              <TableRow key={player.id} className="hover:bg-slate-800/50">
                                <TableCell>
                                  <div>
                                    <p className="font-medium">{player.name}</p>
                                    {player.membership_number && (
                                      <p className="text-xs text-slate-400">#{player.membership_number}</p>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {player.age ? `${player.age} yrs` : '-'}
                                </TableCell>
                                <TableCell>
                                  <div>
                                    <p className="text-sm">{player.parent_name || '-'}</p>
                                    {player.parent_email && (
                                      <a href={`mailto:${player.parent_email}`} className="text-xs text-blue-400 hover:underline">
                                        {player.parent_email}
                                      </a>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {player.parent_phone && (
                                    <a href={`tel:${player.parent_phone}`} className="text-sm text-emerald-400 hover:underline">
                                      {player.parent_phone}
                                    </a>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {(() => {
                                    const pi = paymentStatuses[player.id];
                                    if (!pi || pi.payment_status === 'no_enrollment') return <span className="text-slate-500 text-sm">-</span>;
                                    return pi.paid ? (
                                      <Badge className="bg-emerald-500/20 text-emerald-400 text-xs" data-testid={`roster-paid-${player.id}`}>$ Paid</Badge>
                                    ) : (
                                      <Badge className="bg-red-500/20 text-red-400 text-xs" data-testid={`roster-unpaid-${player.id}`}>Unpaid{pi.outstanding ? ` $${pi.outstanding}` : ''}</Badge>
                                    );
                                  })()}
                                </TableCell>
                                <TableCell>
                                  <Badge className={
                                    player.status === 'active' 
                                      ? 'bg-emerald-500/20 text-emerald-400'
                                      : 'bg-amber-500/20 text-amber-400'
                                  }>
                                    {player.status || 'pending'}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-16 text-slate-400">
                      <Calendar className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p className="text-lg">Select a session to view players</p>
                      <p className="text-sm">Click on a session from the list</p>
                    </div>
                  )}
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Parents View */}
          <TabsContent value="parents" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Parents List */}
              <div className="lg:col-span-1">
                <Card className="bg-slate-900/50 border-slate-700 p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Users className="w-4 h-4 text-emerald-400" />
                      Parents ({filteredParents.length})
                    </h3>
                    {filteredParents.length > 0 && (
                      <button
                        onClick={selectAllParents}
                        className="text-xs text-blue-400 hover:text-blue-300"
                      >
                        {selectedParentIds.length === filteredParents.length ? "Deselect All" : "Select All"}
                      </button>
                    )}
                  </div>

                  {/* Membership tier filter chips */}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {[
                      { key: "all", label: `All · ${parents.length}` },
                      { key: "section", label: `Section · ${tierCounts.section || 0}` },
                      { key: "club_verified", label: `SCC ✓ · ${tierCounts.club_verified || 0}` },
                      { key: "club_pending", label: `SCC pending · ${tierCounts.club_pending || 0}` },
                      { key: "digital", label: `Digital · ${tierCounts.digital || 0}` },
                      { key: "non_member", label: `Non-member · ${tierCounts.non_member || 0}` },
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        onClick={() => setTierFilter(key)}
                        data-testid={`tier-filter-${key}`}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                          tierFilter === key
                            ? "bg-blue-500/30 text-blue-200 border-blue-500/50"
                            : "bg-slate-800/40 text-slate-400 border-slate-700 hover:text-white hover:border-slate-600"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  
                  {/* Bulk Actions */}
                  {selectedParentIds.length > 0 && (
                    <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-blue-300">
                          {selectedParentIds.length} selected
                        </span>
                        <Button
                          size="sm"
                          onClick={sendBulkParentInfoEmail}
                          disabled={sendingBulkEmail}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Send className="w-3 h-3 mr-1" />
                          {sendingBulkEmail ? "Sending..." : "Send Info Emails"}
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {loading ? (
                    <div className="text-center py-8 text-slate-400">Loading...</div>
                  ) : filteredParents.length === 0 ? (
                    <div className="text-center py-8 text-slate-400">No parents found</div>
                  ) : (
                    <div className="space-y-2 max-h-[600px] overflow-y-auto">
                      {filteredParents.map((parent) => (
                        <div
                          key={parent.id}
                          className={`flex items-center gap-2 p-3 rounded-lg border transition-colors ${
                            selectedParent?.id === parent.id
                              ? 'bg-emerald-500/20 border-emerald-500'
                              : 'bg-slate-800/50 border-slate-700 hover:bg-slate-800'
                          }`}
                        >
                          {/* Checkbox */}
                          <input
                            type="checkbox"
                            checked={selectedParentIds.includes(parent.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleParentSelection(parent.id);
                            }}
                            className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
                          />
                          
                          {/* Parent Info Button */}
                          <button
                            onClick={() => handleParentClick(parent)}
                            className="flex-1 text-left"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{parent.name}</p>
                                <p className="text-xs text-slate-400">{parent.email}</p>
                                <MembershipTierBadge parent={parent} compact />
                              </div>
                              <div className="text-right">
                                <span className="text-lg font-bold text-emerald-400">
                                  {parent.children_count || 0}
                                </span>
                                <p className="text-xs text-slate-400">
                                  {parent.children_count === 1 ? 'child' : 'children'}
                                </p>
                              </div>
                            </div>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>

              {/* Parent Detail - Children & Sessions */}
              <div className="lg:col-span-2">
                <Card className="bg-slate-900/50 border-slate-700 p-4">
                  {selectedParent ? (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="font-semibold text-lg">{selectedParent.name}</h3>
                          <div className="flex items-center gap-4 text-sm text-slate-400 mt-1">
                            <a href={`mailto:${selectedParent.email}`} className="flex items-center gap-1 text-blue-400 hover:underline">
                              <Mail className="w-3 h-3" />
                              {selectedParent.email}
                            </a>
                            {selectedParent.phone && (
                              <a href={`tel:${selectedParent.phone}`} className="flex items-center gap-1 text-emerald-400 hover:underline">
                                <Phone className="w-3 h-3" />
                                {selectedParent.phone}
                              </a>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 items-center">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => sendParentInfoEmail(selectedParent.id)}
                            disabled={sendingEmail}
                            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                          >
                            <Send className="w-3 h-3 mr-1" />
                            {sendingEmail ? "Sending..." : "Send Info Email"}
                          </Button>
                          <MembershipTierBadge parent={selectedParent} />
                          {selectedParent.payment_status && (
                            <Badge className={
                              selectedParent.payment_status === 'paid'
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : 'bg-amber-500/20 text-amber-400'
                            }>
                              {selectedParent.payment_status}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {parentChildren.length === 0 ? (
                        <div className="text-center py-12 text-slate-400">
                          <User className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>No children registered</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {parentChildren.map((child) => (
                            <div key={child.id} className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <p className="font-semibold text-lg">{child.name}</p>
                                  <div className="flex items-center gap-3 text-sm text-slate-400 mt-1">
                                    {child.age && <span>{child.age} years old</span>}
                                    {child.gender && <span>• {child.gender}</span>}
                                    {child.membership_number && (
                                      <span className="text-blue-400">• #{child.membership_number}</span>
                                    )}
                                  </div>
                                </div>
                                <Badge className={
                                  child.status === 'active'
                                    ? 'bg-emerald-500/20 text-emerald-400'
                                    : 'bg-amber-500/20 text-amber-400'
                                }>
                                  {child.status || 'pending'}
                                </Badge>
                              </div>
                              
                              {/* Child's Sessions */}
                              {child.sessions && child.sessions.length > 0 && (
                                <div className="mt-3">
                                  <p className="text-xs text-slate-400 mb-2">Enrolled Sessions:</p>
                                  <div className="flex flex-wrap gap-2">
                                    {child.sessions.map((sess, idx) => (
                                      <span key={idx} className="text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded">
                                        <Clock className="w-3 h-3 inline mr-1" />
                                        {sess.day_of_week} {sess.start_time}
                                        {sess.age_group_name && ` (${sess.age_group_name})`}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* Team Assignment */}
                              {child.team_name && (
                                <div className="mt-3">
                                  <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded">
                                    Team: {child.team_name}
                                  </span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-16 text-slate-400">
                      <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p className="text-lg">Select a parent to view their children</p>
                      <p className="text-sm">Click on a parent from the list</p>
                    </div>
                  )}
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
